/**
 * Collects the styles a payload needs, deduplicated by class name.
 *
 * Name is the dedup key because the token registry is the single source of truth — two
 * blocks referencing `lp-h2` must produce one style object, not two. That matters beyond
 * payload size: it is what makes section-by-section pasting work. Paste the hero, then paste
 * the CTA band with ⌘⇧V, and Webflow reuses the `lp-h2` it already has rather than creating
 * `lp-h2-2`.
 */

import { compileStyleLess, getStyle, type StyleDef } from '@lp/tokens';
import { makeId, styleSeed } from './ids.js';
import type { XscpStyle } from './types.js';

export class StyleRegistry {
  private readonly defs = new Map<string, StyleDef>();

  /** Register a class (and, for a combo, its base). Returns the style id. */
  ensure(name: string): string {
    if (!this.defs.has(name)) {
      const def = getStyle(name);
      this.defs.set(name, def);
      // A combo is meaningless without its base, and Webflow needs the base present so the
      // `children` wiring resolves.
      if (def.comb && def.parent) this.ensure(def.parent);
    }
    return makeId(styleSeed(name));
  }

  has(name: string): boolean {
    return this.defs.has(name);
  }

  names(): string[] {
    return [...this.defs.keys()];
  }

  /**
   * Emit the style objects.
   *
   * Bases come before combos, and each base's `children` array lists the ids of the combos
   * derived from it — that wiring is how Webflow knows `.is-tone-indigo` only applies
   * alongside `.lp-section`.
   */
  finalise(): XscpStyle[] {
    const bases: StyleDef[] = [];
    const combos: StyleDef[] = [];
    for (const def of this.defs.values()) (def.comb ? combos : bases).push(def);

    const childrenOf = new Map<string, string[]>();
    for (const combo of combos) {
      if (!combo.parent) continue;
      const list = childrenOf.get(combo.parent) ?? [];
      list.push(makeId(styleSeed(combo.name)));
      childrenOf.set(combo.parent, list);
    }

    const toStyle = (def: StyleDef): XscpStyle => {
      const compiled = compileStyleLess(def);
      return {
        _id: makeId(styleSeed(def.name)),
        fake: false,
        type: 'class',
        name: def.name,
        namespace: '',
        comb: def.comb ? '&' : '',
        styleLess: compiled.styleLess,
        variants: compiled.variants,
        children: childrenOf.get(def.name) ?? [],
        origin: null,
        selector: null,
      };
    };

    // Stable ordering keeps golden snapshots readable.
    const byName = (a: StyleDef, b: StyleDef): number => a.name.localeCompare(b.name);
    return [...bases.sort(byName).map(toStyle), ...combos.sort(byName).map(toStyle)];
  }
}
